import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { RuleCheckPlotResult, RuleCheckRun, SbcApiService } from '../services/sbc-api.service';
import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';
import { SessionService } from '../../../core/services/session.service';

type HistoryRow = RuleCheckPlotResult & {
  runId: number;
  runLabel?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
};

@Component({
  selector: 'app-compliance-runs',
  standalone: true,
  imports: [CommonModule, RouterLink, PageRefreshButtonComponent, PageBackButtonComponent],
  templateUrl: './compliance-runs.component.html',
  styleUrl: './compliance-runs.component.css',
})
export class ComplianceRunsComponent {
  private readonly api = inject(SbcApiService);
  private readonly route = inject(ActivatedRoute);
  readonly session = inject(SessionService);

  runs = signal<RuleCheckRun[]>([]);
  loading = signal(false);
  error = signal('');
  expandedKey = signal<string | null>(null);
  highlightRunId = signal<number | null>(null);

  readonly historyRows = computed((): HistoryRow[] => {
    const byPlot = new Map<string, HistoryRow>();
    for (const run of this.runs()) {
      for (const plot of run.plotResults || []) {
        if (plot.summaryOnly) continue;
        const row: HistoryRow = {
          ...plot,
          runId: run.id,
          runLabel: run.runLabel,
          startedAt: run.startedAt,
          endedAt: run.endedAt,
        };
        const key = String(plot.plotRef || plot.measurementId || row.runId);
        const cur = byPlot.get(key);
        if (!cur || row.runId > cur.runId) {
          byPlot.set(key, row);
        }
      }
    }
    return [...byPlot.values()].sort((a, b) => String(a.plotRef).localeCompare(String(b.plotRef)));
  });

  constructor() {
    effect(() => {
      this.session.authorityId();
      untracked(() => this.reload());
    });

    this.route.queryParamMap.subscribe((params) => {
      const raw = params.get('highlightRun');
      const runId = raw ? Number(raw) : null;
      this.highlightRunId.set(runId && !Number.isNaN(runId) ? runId : null);
    });
  }

  reload(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listRuleCheckRuns().subscribe({
      next: (res) => {
        this.runs.set(res.data || []);
        this.loading.set(false);
        this.applyHighlight();
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message || 'Failed to load rule-check history');
      },
    });
  }

  isHighlighted(row: HistoryRow): boolean {
    return this.highlightRunId() != null && row.runId === this.highlightRunId();
  }

  private applyHighlight(): void {
    const runId = this.highlightRunId();
    if (!runId) return;
    const row = this.historyRows().find((r) => r.runId === runId);
    if (row) {
      this.expandedKey.set(this.rowKey(row));
    }
  }

  rowKey(row: HistoryRow): string {
    return `${row.runId}-${row.plotRef || 'summary'}-${row.measurementId || 0}`;
  }

  toggleRules(row: HistoryRow): void {
    const key = this.rowKey(row);
    this.expandedKey.set(this.expandedKey() === key ? null : key);
  }

  isExpanded(row: HistoryRow): boolean {
    return this.expandedKey() === this.rowKey(row);
  }

  canShowRules(row: HistoryRow): boolean {
    return !row.summaryOnly && (row.rules?.length ?? 0) > 0;
  }

  ruleStatusLabel(status: string): string {
    if (status === 'pass') return 'PASS';
    if (status === 'fail') return 'FAIL';
    if (status === 'skipped') return 'SKIPPED';
    return status;
  }

  referenceLabel(mode?: string | null): string {
    if (mode === 'plan_and_bye_law') return 'Plan + bye-law';
    if (mode === 'bye_law_only') return 'Bye-law only';
    return '—';
  }

  resultLabel(row: HistoryRow): string {
    if (row.summaryOnly) {
      return `Compliant (${row.buildingsChecked ?? 0} building(s) checked)`;
    }
    if (row.compliant) return 'Compliant';
    const n = row.violationCount ?? row.rules?.filter((r) => r.status === 'fail').length ?? 0;
    return n === 1 ? '1 violation found' : `${n} violations found`;
  }

  resultClass(row: HistoryRow): string {
    return row.compliant ? 'rc-result-pass' : 'rc-result-fail';
  }

  ruleStatusClass(status: string): string {
    if (status === 'pass') return 'rc-rule-pass';
    if (status === 'fail') return 'rc-rule-fail';
    return 'rc-rule-skip';
  }

  formatRuleValue(value: unknown, unit?: string): string {
    if (value == null || value === '') return '—';
    return unit ? `${value} ${unit}`.trim() : String(value);
  }
}
